// Override Indicators (CR-20260722-002) — the fieldset ABOVE the tab bar that lets the user hand
// the skill its market conditions instead of having it measure them.
//
// Three boxes in ONE row, uniform treatment, in this order: IV (percent), HS (two decimals),
// VS (integer). The row looks identical regardless of what each value is actually keyed on — the
// market reads IV per expiration and HS as one term-structure ratio for every date, VS per
// expiration AND per side. That the cardinalities differ is an implementation detail and
// deliberately invisible here (owner ruling D2); a value that spans every date simply shows the
// same number as you step through them. Since CR-20260921-002 every OVERRIDE is per side: the
// seed writes one reading into both sides, the trader may then split them.
// The order is the owner's (D19) and is set by DOM order in the template — the flex row carries no
// positional selectors, so the template is the single place it is decided.
//
// Arrows appear ONLY in Date Range mode, stepping the range one date at a time (D3).
//
// READING vs OVERRIDE is the whole contract. The parent feeds the boxes with live market readings
// for the anchored date; that is DISPLAY, not override. Only a user edit produces an override, and
// it promotes the entire record — including the two boxes the user did not touch (D7). Hence the
// `!e.event` guard on every handler: DevExtreme populates `e.event` only for genuine interaction,
// so a programmatic seed write is distinguishable from a keystroke. Without it every seeded date
// would mark itself edited and silently ship as an override — a failure that looks completely
// correct on screen.

import {ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output} from '@angular/core';
import {DetectMethodChanges} from '../utils';
import {SessionService} from '../authentication/session-service.service';
import {copyOverride, isOverrideValueSupplied, MarketConditionOverride, OpgSide, OverrideChangedKeys} from './opg-copilot-domain';

@Component({
    selector: 'ets-opg-override-indicators',
    templateUrl: './opg-override-indicators.component.html',
    styleUrls: ['./opg-override-indicators.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class OpgOverrideIndicatorsComponent {

    // @DetectMethodChanges reads this['_changeDetector'] in its finally block; this was the
    // ONE copilot component missing it, so every decorated handler here logged
    // "No _changeDetector" to the console and skipped its own OnPush CD pass (a gap born
    // with the component in OPG, surfaced at the 2026-08-04 smoke's console check).
    constructor(private readonly _changeDetector: ChangeDetectorRef,
                private readonly _sessionService: SessionService) {
    }

    // The record for the date currently on screen: this tab's override if it has one, otherwise
    // the SHARED market reading. NEVER written to — the handlers emit a copy (see promote), because
    // a reading is the very object every other tab showing this date is rendering.
    @Input() value: MarketConditionOverride | null = null;

    // Does any date in this tab's range carry an override (owner ruling D21)? Drives the FRAME and
    // the legend only. It answers a question the per-date colouring cannot: "did I edit another
    // date in this range and forget?" — the frame stays lit while you step across clean dates.
    @Input() rangeOverridden = false;

    // Which side's values this fieldset edits. In Calls & Puts two instances render side by side,
    // one per side, and EVERY box is that side's own (CR-20260921-002, client ruling 2026-09-21
    // replacing D5): editing the Calls IV never moves the Puts IV. The seed writes one market
    // reading into both sides, so they agree until the trader says otherwise.
    @Input() side: OpgSide = 'calls';

    // null in single-side mode (no side suffix needed), 'calls'/'puts' in the duo layout.
    @Input() sideLabel: string | null = null;

    // Formatted date the values belong to — or "All dates" on the aggregate position.
    @Input() dateLabel: string | null = null;

    // PROVENANCE (owner ruling 2026-09-17: "always show the date hint if you're showing market
    // values, the user must know where they are coming from").
    //
    // Set ONLY on the "All dates" position, and only while the numbers on screen are sourced from
    // one specific date — the live feed streams exactly one date at a time, so an unedited "All
    // dates" is always showing the FIRST date's reading. Rendering that under a bare aggregate
    // label would present a one-date reading as if it held for the whole range: a screen that
    // looks correct and is not.
    //
    // The host clears it once an All-dates edit has stamped every date with the same record —
    // there is then no source date to name, because the values genuinely are true of all of them.
    // The host decides that by PROOF (isUniformAcrossDates), never by assuming an edit happened.
    @Input() sourceDateLabel: string | null = null;

    // Date Range mode only (D3).
    @Input() showArrows = false;
    @Input() canPrev = false;
    @Input() canNext = false;

    @Input() disabled = false;

    // CR-20260831-004: which of the anchored date's values changed since this tab's last run
    // (resolved by the orchestrator's diff — the child does not know the key grammar). Both sides
    // are carried because only this component knows which side its boxes show.
    // null = clean. Distinct from `overridden` (manual edit, yellow border/text): a box can be
    // manually overridden AND unchanged-since-run, or changed-since-run without being overridden
    // (the run sent an override this box has since cleared).
    @Input() changedKeys: OverrideChangedKeys | null = null;

    get ivChanged(): boolean {
        if (!this.changedKeys) {
            return false;
        }
        return this.side === 'calls' ? this.changedKeys.ivCall : this.changedKeys.ivPut;
    }

    get hsChanged(): boolean {
        if (!this.changedKeys) {
            return false;
        }
        return this.side === 'calls' ? this.changedKeys.hsCall : this.changedKeys.hsPut;
    }

    get vsChanged(): boolean {
        if (!this.changedKeys) {
            return false;
        }
        return this.side === 'calls' ? this.changedKeys.vsCall : this.changedKeys.vsPut;
    }

    // Super-user gate (2026-08-27 owner ruling): only super users may edit the override boxes.
    // Read-only IN PLACE for everyone else — values stay visible at normal contrast (readOnly,
    // not disabled, so no dx-state-disabled dimming), but boxes, arrows, clear link and the
    // dblclick spinning-takeover are all inert. Frontend-only by owner decision.
    get superUserReadOnly(): boolean {
        return !this._sessionService.isSuperUser;
    }

    // Everything-inert predicate for the handlers: run-in-flight/host disable OR the role gate.
    get effectiveDisabled(): boolean {
        return this.disabled || this.superUserReadOnly;
    }

    @Output() valueChange = new EventEmitter<MarketConditionOverride>();
    @Output() prevDate = new EventEmitter<void>();
    @Output() nextDate = new EventEmitter<void>();
    @Output() clearDate = new EventEmitter<void>();

    // CR-20260831-005: the focus freeze. While any of the three boxes holds focus, the DISPLAY is
    // pinned to the record captured at focus time, so a live market tick can no longer repaint the
    // box under the user's cursor and wipe what they are typing (dx-number-box only commits on
    // blur/Enter — mid-typing text lives in the DOM alone and does not survive a [value] repaint).
    // The freeze is per-INSTANCE: in the Calls & Puts duo only the focused side's fieldset freezes
    // (owner ruling), and it freezes all three of its boxes together, not just the focused one.
    // The parent's live feed keeps running untouched — the VS subscription is ref-counted on the
    // backend and must not churn on focus/blur — so on unfreeze the boxes catch up to the latest
    // reading on the very next render. A committed edit re-points the snapshot (see promote), so
    // an Enter-commit while still focused shows the typed value, not the pre-edit one.
    private _frozen: MarketConditionOverride | null = null;

    get displayIv(): number | null {
        const v = this._frozen || this.value;
        if (!v) {
            return null;
        }
        return this.side === 'calls' ? v.ivCall : v.ivPut;
    }

    get displayHs(): number | null {
        const v = this._frozen || this.value;
        if (!v) {
            return null;
        }
        return this.side === 'calls' ? v.hsCall : v.hsPut;
    }

    get displayVs(): number | null {
        const v = this._frozen || this.value;
        if (!v) {
            return null;
        }
        return this.side === 'calls' ? v.vsCall : v.vsPut;
    }

    @DetectMethodChanges()
    onValuesFocusIn(): void {
        // A read-only box (non-super-user) still takes focus but cannot be edited — freezing its
        // display would stop the ticking for no reason, so the gate stays live there.
        if (this._frozen || this.effectiveDisabled || !this.value) {
            return;
        }
        this._frozen = this.value;
    }

    @DetectMethodChanges()
    onValuesFocusOut(e: FocusEvent): void {
        if (!this._frozen) {
            return;
        }
        // Focus moving BETWEEN the boxes of this fieldset keeps the freeze; anything else releases
        // it. The date arrows and clear link are non-focusable spans, so clicking them blurs with
        // relatedTarget null and releases too (owner ruling: a date step counts as blur). An
        // unchanged value simply resumes ticking; a changed one has already been promoted to an
        // override by its own change handler.
        const row = e.currentTarget as HTMLElement;
        const to = e.relatedTarget as Node | null;
        if (to && row && row.contains(to)) {
            return;
        }
        this._frozen = null;
    }

    get legend(): string {
        return this.sideLabel ? 'Override Indicators · ' + this.sideLabel : 'Override Indicators';
    }

    // THIS date is overridden: yellow on the date label and the three values (D9, narrowed by D21 —
    // the frame and legend are driven by rangeOverridden instead, so stepping onto a clean date
    // clears the numbers without clearing the reminder that the range holds edits).
    get overridden(): boolean {
        return this.value != null && this.value.edited;
    }

    // Per-box spinners: the two seeds land independently (IV is a cached query, vertical skew may
    // warm a backend slot for seconds), so one shared flag would leave the IV box spinning until
    // the slower call finished.
    get ivLoading(): boolean {
        return this.value != null && this.value.ivLoading;
    }

    get vsLoading(): boolean {
        return this.value != null && this.value.vsLoading;
    }

    // A required value this date does not carry — EMPTY, ZERO or NEGATIVE (owner ruling D22,
    // 2026-07-22). Red border on the offending box, and the parent runs the same predicate across
    // the whole range to disable the Run button, so the red is never the only signal.
    //
    // The box stays EMPTY when missing — never a placeholder zero. D15's reasoning still holds:
    // 0 is a legitimate vertical-skew reading (it classifies Flat), so a filler zero would quietly
    // change which structures the engine returns instead of signalling "missing". D22 goes one
    // further and refuses a TYPED zero as well.
    //
    // Only once the date is being overridden. On a purely seeded record a null just means the
    // market had no reading; nothing is required of the user yet, so red would be noise.
    get ivInvalid(): boolean {
        return this.overridden && !isOverrideValueSupplied(this.iv);
    }

    // HS is now in this set. It used to be excluded because no backend path consumes horizontal
    // skew (D4) — but D4 governs what the WIRE carries, not whether the user must fill the box,
    // and D22 makes every box mandatory once a date is overridden.
    get hsInvalid(): boolean {
        return this.overridden && !isOverrideValueSupplied(this.hs);
    }

    get vsInvalid(): boolean {
        return this.overridden && !isOverrideValueSupplied(this.vs);
    }

    // This side's three values, unfrozen (the validity cues read the record, not the display).
    get iv(): number | null {
        if (!this.value) {
            return null;
        }
        return this.side === 'calls' ? this.value.ivCall : this.value.ivPut;
    }

    get hs(): number | null {
        if (!this.value) {
            return null;
        }
        return this.side === 'calls' ? this.value.hsCall : this.value.hsPut;
    }

    get vs(): number | null {
        if (!this.value) {
            return null;
        }
        return this.side === 'calls' ? this.value.vsCall : this.value.vsPut;
    }

    // Nothing to clear on a date the user never touched.
    get canClear(): boolean {
        return this.overridden && !this.effectiveDisabled;
    }

    @DetectMethodChanges()
    onIvChanged(e: { value?: number; event?: unknown }): void {
        if (!this.isUserEdit(e)) {
            return;
        }
        const v = this.toNumberOrNull(e.value);
        this.promote(o => {
            if (this.side === 'calls') {
                o.ivCall = v;
            } else {
                o.ivPut = v;
            }
        });
    }

    @DetectMethodChanges()
    onVsChanged(e: { value?: number; event?: unknown }): void {
        if (!this.isUserEdit(e)) {
            return;
        }
        const v = this.toNumberOrNull(e.value);
        this.promote(o => {
            if (this.side === 'calls') {
                o.vsCall = v;
            } else {
                o.vsPut = v;
            }
        });
    }

    @DetectMethodChanges()
    onHsChanged(e: { value?: number; event?: unknown }): void {
        if (!this.isUserEdit(e)) {
            return;
        }
        const v = this.toNumberOrNull(e.value);
        this.promote(o => {
            if (this.side === 'calls') {
                o.hsCall = v;
            } else {
                o.hsPut = v;
            }
        });
    }

    @DetectMethodChanges()
    onPrev(): void {
        if (this.canPrev && !this.effectiveDisabled) {
            this.prevDate.emit();
        }
    }

    @DetectMethodChanges()
    onNext(): void {
        if (this.canNext && !this.effectiveDisabled) {
            this.nextDate.emit();
        }
    }

    @DetectMethodChanges()
    onClear(): void {
        if (this.canClear) {
            this.clearDate.emit();
        }
    }

    // CR-20260723-002: take manual control of a SPINNING box. A vertical-skew slot can take seconds to
    // warm — or never resolve on a dead feed — and a loading box is disabled, so without this the user
    // could be permanently unable to override it. Double-clicking a spinning box FREEZES the current
    // (possibly partial) reading as an override: promote() sets edited=true and clears BOTH loading
    // flags, so every spinner stops and the boxes become editable. Any box that already loaded keeps its
    // value; the stuck one(s) come through null and D22 requires the user to fill them. No-op when
    // nothing is loading (the box is already editable) or while the whole block is disabled (run in
    // flight).
    @DetectMethodChanges()
    onSpinningTakeover(): void {
        if (this.effectiveDisabled || !this.value) {
            return;
        }
        if (!this.value.ivLoading && !this.value.vsLoading) {
            return;
        }
        this.promote(() => { /* keep the partial reading as-is; promote freezes it and stops loading */ });
    }

    // The echo guard. `e.event` is present only when a human drove the change; a programmatic
    // [value] write-back (the seed) leaves it undefined. See the class header for why this is the
    // difference between a working feature and one that overrides everything silently.
    private isUserEdit(e: { event?: unknown }): boolean {
        return !!e && !!e.event && !!this.value && !this.effectiveDisabled;
    }

    // One edited box promotes the WHOLE date (D7) — the two values the user did not touch were
    // seeded from live readings and are, from now on, supplied values.
    //
    // COPIES, never mutates. `value` may be the shared market reading, which every tab showing this
    // date is rendering; writing into it would edit their display too and would leave the parent
    // unable to tell a reading from an override (D20). The copy is what becomes this tab's record.
    private promote(mutate: (o: MarketConditionOverride) => void): void {
        // A value the user typed is never loading; any spinner the reading carried is over.
        const next = copyOverride(this.value, {edited: true, ivLoading: false, vsLoading: false});
        mutate(next);
        // CR-20260831-005: a commit while still focused (Enter) must show the typed value — leave
        // the freeze in place but re-point it at the promoted record.
        if (this._frozen) {
            this._frozen = next;
        }
        this.valueChange.emit(next);
    }

    private toNumberOrNull(v: number | undefined): number | null {
        return (typeof v === 'number' && isFinite(v)) ? v : null;
    }
}
